import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { JobService } from '../../../core/services/job.service';
import { DealerService } from '../../../core/services/dealer.service';
import { TechnicianService } from '../../../core/services/technician.service';
import { VehicleService } from '../../../core/services/vehicle.service';
import { PartService } from '../../../core/services/part.service';
import { JobTypeService } from '../../../core/services/job-type.service';
import { Job, JobStatus } from '../../../core/models/job.model';
import { Dealer } from '../../../core/models/dealer.model';
import { ServiceAdvisorRequest } from '../../../core/models/service-advisor.model';
import { Technician } from '../../../core/models/technician.model';
import { Vehicle } from '../../../core/models/vehicle.model';
import { Part } from '../../../core/models/part.model';
import { JobType } from '../../../core/models/job-type.model';

interface DraftPartLine {
  partId: number;
  quantity: number;
}

interface JobTypeGroup {
  category: string;
  types: JobType[];
}

@Component({
  selector: 'app-job-list',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './job-list.html',
  styleUrl: './job-list.scss',
})
export class JobList {
  private readonly jobService = inject(JobService);
  private readonly dealerService = inject(DealerService);
  private readonly technicianService = inject(TechnicianService);
  private readonly vehicleService = inject(VehicleService);
  private readonly partService = inject(PartService);
  private readonly jobTypeService = inject(JobTypeService);
  private readonly fb = inject(FormBuilder);

  protected readonly jobs = signal<Job[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly totalCount = signal(0);
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));

  protected readonly statusFilter = signal('');
  protected readonly statusOptions: JobStatus[] = ['Created', 'Assigned', 'Scheduled', 'EnRoute', 'Arrived', 'InProgress', 'Completed', 'Cancelled'];

  protected readonly technicians = signal<Technician[]>([]);
  protected readonly vehicles = signal<Vehicle[]>([]);
  protected readonly parts = signal<Part[]>([]);
  protected readonly jobTypes = signal<JobType[]>([]);

  protected readonly jobTypeGroups = computed<JobTypeGroup[]>(() => {
    const groups = new Map<string, JobType[]>();
    for (const jobType of this.jobTypes()) {
      const category = jobType.category ?? 'Other';
      const list = groups.get(category) ?? [];
      list.push(jobType);
      groups.set(category, list);
    }
    return Array.from(groups.entries()).map(([category, types]) => ({ category, types }));
  });

  protected readonly modalOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly editingJob = signal<Job | null>(null);

  protected readonly historyModalOpen = signal(false);
  protected readonly historyLoading = signal(false);
  protected readonly historyJob = signal<Job | null>(null);
  protected readonly historyBeforeNotesExpanded = signal(false);
  protected readonly historyAfterNotesExpanded = signal(false);
  private static readonly LONG_NOTES_THRESHOLD = 140;

  protected readonly allDealers = signal<Dealer[]>([]);
  protected readonly dealersLoading = signal(false);
  protected readonly dealerSearchTerm = signal('');
  protected readonly dealerFieldFocused = signal(false);
  protected readonly selectedDealer = signal<Dealer | null>(null);
  private dealerBlurHandle?: ReturnType<typeof setTimeout>;

  protected readonly dealerSearchResults = computed(() => {
    const term = this.dealerSearchTerm().trim().toLowerCase();
    const list = this.allDealers();
    if (!term) return list;
    return list.filter((d) => (d.dealerName ?? '').toLowerCase().includes(term));
  });

  protected readonly dealerFilterId = signal<number | null>(null);
  protected readonly dealerFilterSearchTerm = signal('');
  protected readonly dealerFilterFieldFocused = signal(false);
  private dealerFilterBlurHandle?: ReturnType<typeof setTimeout>;

  protected readonly dealerFilterSearchResults = computed(() => {
    const term = this.dealerFilterSearchTerm().trim().toLowerCase();
    const list = this.allDealers();
    if (!term) return list;
    return list.filter((d) => (d.dealerName ?? '').toLowerCase().includes(term));
  });

  protected readonly dealerForm = this.fb.nonNullable.group({
    dealerName: [''],
    email: [''],
    streetName: [''],
    city: [''],
    state: [''],
    zipCode: [''],
    supervisorName: [''],
    phNo: [''],
    laneCount: [''],
    csatRating: [''],
    customerStatus: [''],
    traxtionDevice: [''],
    serviceAdvisorFirstName: [''],
    serviceAdvisorLastName: [''],
    serviceAdvisorEmail: [''],
  });

  private static readonly PM_JOB_TYPE_NAME = 'Preventive Maintenance';

  private static readonly LOCKED_DEALER_FIELDS = [
    'dealerName', 'email', 'streetName', 'city', 'state', 'zipCode', 'supervisorName',
    'phNo', 'laneCount', 'csatRating', 'customerStatus', 'traxtionDevice',
  ] as const;

  protected readonly serviceAdvisors = signal<ServiceAdvisorRequest[]>([]);
  protected readonly advisorsLoading = signal(false);
  protected readonly advisorsLoadFailed = signal(false);
  private readonly advisorsLoaded = signal(false);

  protected readonly dealerHasNoEmail = computed(() => {
    const dealer = this.selectedDealer();
    if (!dealer || this.advisorsLoading() || !this.advisorsLoaded()) return false;

    const hasAdvisorEmail = this.serviceAdvisors().some((a) => (a.email ?? '').trim() !== '');
    return !hasAdvisorEmail
      && (dealer.serviceAdvisorEmail ?? '').trim() === ''
      && (dealer.email ?? '').trim() === '';
  });

  protected readonly draftTechnicianId = signal<number | null>(null);
  protected readonly draftVehicleId = signal<number | null>(null);
  protected readonly draftJobTypeIds = signal<number[]>([]);
  protected readonly draftScheduledDate = signal('');
  protected readonly draftNotes = signal('');
  protected readonly draftParts = signal<DraftPartLine[]>([]);

  protected readonly availableVehicleIds = signal<Set<number> | null>(null);

  protected readonly draftTotal = computed(() => {
    const partMap = new Map(this.parts().map((p) => [p.partId, p]));
    return this.draftParts().reduce((sum, line) => {
      const part = line.partId != null ? partMap.get(line.partId) : undefined;
      return sum + (part?.unitPrice ?? 0) * (line.quantity || 0);
    }, 0);
  });

  constructor() {
    this.loadJobs();
    this.technicianService.getTechnicians({ pageSize: 200 }).subscribe({ next: (r) => this.technicians.set(r.items.filter((t) => t.isActive)) });
    this.vehicleService.getVehicles({ pageSize: 200 }).subscribe({ next: (r) => this.vehicles.set(r.items) });
    this.partService.getParts({ pageSize: 200 }).subscribe({ next: (r) => this.parts.set(r.items.filter((p) => p.isActive)) });
    this.jobTypeService.getJobTypes().subscribe({ next: (r) => this.jobTypes.set(r.items) });
  }

  private loadJobs(): void {
    this.loading.set(true);
    this.error.set(null);
    this.jobService.getJobs({
      page: this.page(),
      pageSize: this.pageSize(),
      status: (this.statusFilter() || undefined) as JobStatus | undefined,
      dealerId: this.dealerFilterId() ?? undefined,
    }).subscribe({
      next: (result) => {
        this.jobs.set(result.items);
        this.totalCount.set(result.totalCount);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load jobs. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected onStatusFilterChange(value: string): void {
    this.statusFilter.set(value);
    this.page.set(1);
    this.loadJobs();
  }

  protected onDealerFilterSearchInput(value: string): void {
    this.dealerFilterSearchTerm.set(value);
    if (!value && this.dealerFilterId() !== null) {
      this.dealerFilterId.set(null);
      this.page.set(1);
      this.loadJobs();
    }
  }

  protected onDealerFilterFocus(): void {
    clearTimeout(this.dealerFilterBlurHandle);
    this.dealerFilterFieldFocused.set(true);
    this.ensureDealersLoaded();
  }

  protected onDealerFilterBlur(): void {
    this.dealerFilterBlurHandle = setTimeout(() => this.dealerFilterFieldFocused.set(false), 150);
  }

  protected selectDealerFilter(dealer: Dealer): void {
    this.applyDealerFilter(dealer.dealerId, dealer.dealerName ?? '');
  }

  protected clearDealerFilter(): void {
    this.dealerFilterId.set(null);
    this.dealerFilterSearchTerm.set('');
    this.page.set(1);
    this.loadJobs();
  }

  private applyDealerFilter(dealerId: number, dealerName: string): void {
    this.dealerFilterId.set(dealerId);
    this.dealerFilterSearchTerm.set(dealerName);
    this.dealerFilterFieldFocused.set(false);
    this.page.set(1);
    this.loadJobs();
  }

  private ensureDealersLoaded(): void {
    if (this.allDealers().length > 0 || this.dealersLoading()) return;
    this.dealersLoading.set(true);
    this.dealerService.getDealers({ page: 1, pageSize: 3000 }).subscribe({
      next: (result) => {
        this.allDealers.set(result.items);
        this.dealersLoading.set(false);
      },
      error: (err) => {
        this.dealersLoading.set(false);
        console.error(err);
      }
    });
  }

  protected goToPreviousPage(): void {
    if (this.page() > 1) {
      this.page.update((p) => p - 1);
      this.loadJobs();
    }
  }

  protected goToNextPage(): void {
    if (this.page() < this.totalPages()) {
      this.page.update((p) => p + 1);
      this.loadJobs();
    }
  }

  protected openDispatchModal(prefillDealer?: Dealer): void {
    this.editingJob.set(null);
    this.selectedDealer.set(null);
    this.dealerSearchTerm.set('');
    this.dealerFieldFocused.set(false);
    this.dealerForm.reset();
    this.applyDealerLock(false);
    this.serviceAdvisors.set([]);
    this.advisorsLoaded.set(false);
    this.advisorsLoadFailed.set(false);
    this.draftTechnicianId.set(null);
    this.draftVehicleId.set(null);
    this.draftJobTypeIds.set([]);
    this.draftScheduledDate.set('');
    this.draftNotes.set('');
    this.draftParts.set([]);
    this.availableVehicleIds.set(null);
    this.modalOpen.set(true);

    if (prefillDealer) {
      this.selectDealer(prefillDealer);
    }

    this.ensureDealersLoaded();
  }

  protected openHistoryModal(job: Job): void {
    this.historyJob.set(job);
    this.historyLoading.set(true);
    this.historyModalOpen.set(true);
    this.historyBeforeNotesExpanded.set(false);
    this.historyAfterNotesExpanded.set(false);

    this.jobService.getJobById(job.jobId).subscribe({
      next: (full) => {
        this.historyJob.set(full);
        this.historyLoading.set(false);
      },
      error: (err) => {
        this.historyLoading.set(false);
        console.error(err);
      }
    });
  }

  protected closeHistoryModal(): void {
    this.historyModalOpen.set(false);
    this.historyJob.set(null);
  }

  protected newDispatchFromHistory(): void {
    const job = this.historyJob();
    if (!job) return;

    this.dealerService.getDealerById(job.dealerId).subscribe({
      next: (dealer) => {
        this.closeHistoryModal();
        this.openDispatchModal(dealer);
      },
      error: (err) => console.error(err)
    });
  }

  protected photosFor(type: 'Before' | 'After'): { jobPhotoId: number; blobUrl: string | null }[] {
    return (this.historyJob()?.photos ?? []).filter((p) => p.photoType === type);
  }

  protected isLongNotes(notes: string | null): boolean {
    return !!notes && notes.length > JobList.LONG_NOTES_THRESHOLD;
  }

  protected viewDealerHistoryFromModal(): void {
    const job = this.historyJob();
    if (!job) return;
    this.closeHistoryModal();
    this.applyDealerFilter(job.dealerId, job.dealerName ?? '');
  }

  protected openEditJobModal(job: Job): void {
    this.editingJob.set(job);
    this.selectedDealer.set(null);
    this.draftTechnicianId.set(job.technicianId ?? null);
    this.draftVehicleId.set(job.vehicleId ?? null);
    this.draftJobTypeIds.set(job.jobTypes?.map((jt) => jt.jobTypeId) ?? []);
    this.draftScheduledDate.set(this.toDatetimeLocal(job.scheduledDate));
    this.draftNotes.set(job.notes ?? '');
    this.draftParts.set((job.parts ?? []).map((p) => ({ partId: p.partId, quantity: p.quantity })));
    this.availableVehicleIds.set(null);
    this.modalOpen.set(true);
    this.refreshAvailableVehicles();
  }

  private toDatetimeLocal(iso: string | null): string {
    return iso ? iso.slice(0, 16) : '';
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
    this.editingJob.set(null);
  }

  protected onScheduledDateChange(value: string): void {
    this.draftScheduledDate.set(value);
    this.refreshAvailableVehicles();
  }

  protected toggleJobType(jobTypeId: number): void {
    this.draftJobTypeIds.update((ids) =>
      ids.includes(jobTypeId) ? ids.filter((id) => id !== jobTypeId) : [...ids, jobTypeId]
    );
    this.refreshAvailableVehicles();
  }

  private refreshAvailableVehicles(): void {
    const scheduledDate = this.draftScheduledDate();
    const selectedIds = new Set(this.draftJobTypeIds());
    const durationMinutes = this.jobTypes()
      .filter((jt) => selectedIds.has(jt.jobTypeId))
      .reduce((sum, jt) => sum + (jt.durationMinutes ?? 0), 0) || null;

    if (!scheduledDate || !durationMinutes) {
      this.availableVehicleIds.set(null);
      return;
    }

    this.vehicleService.getAvailableVehicles(scheduledDate, durationMinutes, this.editingJob()?.jobId).subscribe({
      next: (available) => this.availableVehicleIds.set(new Set(available.map((v) => v.vehicleId))),
      error: (err) => console.error(err)
    });
  }

  protected isVehicleAvailable(vehicleId: number): boolean {
    const available = this.availableVehicleIds();
    if (available === null) return true;
    return available.has(vehicleId) || this.draftVehicleId() === vehicleId;
  }

  protected onDealerSearchInput(value: string): void {
    this.dealerSearchTerm.set(value);
    this.selectedDealer.set(null);
  }

  protected onDealerFocus(): void {
    clearTimeout(this.dealerBlurHandle);
    this.dealerFieldFocused.set(true);
  }

  protected onDealerBlur(): void {
    this.dealerBlurHandle = setTimeout(() => this.dealerFieldFocused.set(false), 150);
  }

  protected selectDealer(dealer: Dealer): void {
    this.selectedDealer.set(dealer);
    this.dealerSearchTerm.set(dealer.dealerName ?? '');
    this.dealerFieldFocused.set(false);
    this.dealerForm.reset({
      dealerName: dealer.dealerName ?? '',
      email: dealer.email ?? '',
      streetName: dealer.streetName ?? '',
      city: dealer.city ?? '',
      state: dealer.state ?? '',
      zipCode: dealer.zipCode ?? '',
      supervisorName: dealer.supervisorName ?? '',
      phNo: dealer.phNo ?? '',
      laneCount: dealer.laneCount ?? '',
      csatRating: dealer.csatRating ?? '',
      customerStatus: dealer.customerStatus ?? '',
      traxtionDevice: dealer.traxtionDevice ?? '',
      serviceAdvisorFirstName: dealer.serviceAdvisorFirstName ?? '',
      serviceAdvisorLastName: dealer.serviceAdvisorLastName ?? '',
      serviceAdvisorEmail: dealer.serviceAdvisorEmail ?? '',
    });
    this.applyDealerLock(true);
    this.loadServiceAdvisors(dealer.dealerId);
  }

  private applyDealerLock(locked: boolean): void {
    for (const field of JobList.LOCKED_DEALER_FIELDS) {
      const control = this.dealerForm.get(field);
      if (locked) control?.disable({ emitEvent: false });
      else control?.enable({ emitEvent: false });
    }
  }

  private loadServiceAdvisors(dealerId: number): void {
    this.serviceAdvisors.set([]);
    this.advisorsLoaded.set(false);
    this.advisorsLoadFailed.set(false);
    if (!dealerId) return;

    this.advisorsLoading.set(true);
    this.dealerService.getServiceAdvisors(dealerId).subscribe({
      next: (result) => {
        this.serviceAdvisors.set(result.items.map((a) => ({
          firstName: a.firstName ?? '',
          lastName: a.lastName ?? '',
          email: a.email ?? '',
          phone: a.phone ?? '',
        })));
        this.advisorsLoading.set(false);
        this.advisorsLoaded.set(true);
      },
      error: (err) => {
        this.advisorsLoading.set(false);
        this.advisorsLoadFailed.set(true);
        console.error('Failed to load service advisors', err);
      }
    });
  }

  protected addAdvisorLine(): void {
    this.serviceAdvisors.update((list) => [...list, { firstName: '', lastName: '', email: '', phone: '' }]);
  }

  protected removeAdvisorLine(index: number): void {
    this.serviceAdvisors.update((list) => list.filter((_, i) => i !== index));
  }

  protected updateAdvisorField(index: number, field: keyof ServiceAdvisorRequest, value: string): void {
    this.serviceAdvisors.update((list) => list.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  }

  protected isPartSelected(partId: number): boolean {
    return this.draftParts().some((line) => line.partId === partId);
  }

  protected partQuantity(partId: number): number {
    return this.draftParts().find((line) => line.partId === partId)?.quantity ?? 1;
  }

  protected togglePart(partId: number): void {
    this.draftParts.update((lines) =>
      lines.some((line) => line.partId === partId)
        ? lines.filter((line) => line.partId !== partId)
        : [...lines, { partId, quantity: 1 }]
    );
  }

  protected updatePartQuantity(partId: number, quantity: string): void {
    this.draftParts.update((lines) => lines.map((line) => (line.partId === partId ? { ...line, quantity: Number(quantity) || 1 } : line)));
  }

  protected partUnitPrice(partId: number | null): number | null {
    if (partId == null) return null;
    return this.parts().find((p) => p.partId === partId)?.unitPrice ?? null;
  }

  protected saveDispatch(): void {
    const editing = this.editingJob();
    if (editing) {
      this.updateJobRecord(editing.jobId);
    } else {
      this.createJob();
    }
  }

  private updateJobRecord(jobId: number): void {
    const parts = this.draftParts()
      .filter((line) => line.partId != null && line.quantity > 0)
      .map((line) => ({ partId: line.partId as number, quantity: line.quantity }));

    this.saving.set(true);
    this.jobService.updateJob(jobId, {
      technicianId: this.draftTechnicianId() ?? undefined,
      vehicleId: this.draftVehicleId() ?? undefined,
      jobTypeIds: this.draftJobTypeIds(),
      scheduledDate: this.draftScheduledDate() || undefined,
      notes: this.draftNotes() || undefined,
      parts,
    }).subscribe({
      next: (updated) => {
        this.jobs.update((list) => list.map((j) => (j.jobId === updated.jobId ? updated : j)));
        this.saving.set(false);
        this.closeModal();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err.error?.error || 'Failed to update job.');
        console.error(err);
      }
    });
  }

  private createJob(): void {
    const dealer = this.selectedDealer();
    if (!dealer) {
      this.error.set('Select a dealer before creating the job.');
      return;
    }

    this.saving.set(true);

    if (!this.advisorsLoaded()) {
      this.createJobRecord(dealer.dealerId);
      return;
    }

    const advisors = this.serviceAdvisors().filter((a) => a.firstName || a.lastName || a.email);
    this.dealerService.saveServiceAdvisors(dealer.dealerId, advisors).subscribe({
      next: () => this.createJobRecord(dealer.dealerId),
      error: (err) => {
        this.saving.set(false);
        this.error.set('Failed to save service advisors.');
        console.error(err);
      }
    });
  }

  private createJobRecord(dealerId: number): void {
    const parts = this.draftParts()
      .filter((line) => line.partId != null && line.quantity > 0)
      .map((line) => ({ partId: line.partId as number, quantity: line.quantity }));

    this.jobService.createJob({
      dealerId,
      technicianId: this.draftTechnicianId() ?? undefined,
      vehicleId: this.draftVehicleId() ?? undefined,
      jobTypeIds: this.draftJobTypeIds(),
      scheduledDate: this.draftScheduledDate() || undefined,
      notes: this.draftNotes() || undefined,
      parts,
    }).subscribe({
      next: (created) => {
        this.jobs.update((list) => [created, ...list]);
        this.totalCount.update((c) => c + 1);
        this.saving.set(false);
        this.closeModal();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err.error?.error || 'Failed to create job.');
        console.error(err);
      }
    });
  }

  protected canEdit(job: Job): boolean {
    return job.status !== 'Completed' && job.status !== 'Cancelled';
  }

  protected canMarkScheduled(job: Job): boolean {
    return job.status === 'Assigned' && !!job.scheduledDate && !this.isPreventiveMaintenanceOnly(job);
  }

  private isPreventiveMaintenanceOnly(job: Job): boolean {
    const types = job.jobTypes ?? [];
    return types.length > 0 && types.every((jt) => jt.name === JobList.PM_JOB_TYPE_NAME);
  }

  protected markScheduled(job: Job): void {
    this.jobService.updateJobStatus(job.jobId, 'Scheduled').subscribe({
      next: (updated) => this.jobs.update((list) => list.map((j) => (j.jobId === updated.jobId ? updated : j))),
      error: (err) => {
        this.error.set(err.error?.error || 'Failed to mark job Scheduled.');
        console.error(err);
      }
    });
  }

  protected canPause(job: Job): boolean {
    return job.status === 'InProgress' && !!job.workStartedAt;
  }

  protected pauseJob(job: Job): void {
    this.jobService.pauseJob(job.jobId).subscribe({
      next: (updated) => this.jobs.update((list) => list.map((j) => (j.jobId === updated.jobId ? updated : j))),
      error: (err) => {
        this.error.set(err.error?.error || 'Failed to pause job.');
        console.error(err);
      }
    });
  }

  protected statusColor(status: JobStatus): string {
    switch (status) {
      case 'Created': return '#64748B';
      case 'Assigned': return '#00B9F2';
      case 'Scheduled': return '#14b8a6';
      case 'EnRoute': return '#F29727';
      case 'Arrived': return '#a855f7';
      case 'InProgress': return '#eab308';
      case 'Completed': return '#8DC63F';
      case 'Cancelled': return '#ef4444';
      default: return '#64748B';
    }
  }

}
